import { Outlet, Route, type RouteObject } from "react-router";
import PatientAuthGuard from "./ui/components/PatientAuthGuard";
import RequireCurrentConsent from "./ui/components/RequireCurrentConsent";
import PortalLayout from "./ui/components/PortalLayout";
import SessionAgreementsRedirect from "./ui/components/SessionAgreementsRedirect";
import PortalDashboard from "./ui/pages/PortalDashboard";
import AcceptInvitationPage from "./ui/pages/AcceptInvitationPage";
import ConsentPage from "./ui/pages/ConsentPage";
import PatientNotesPage from "./ui/pages/PatientNotesPage";
import PatientDataPage from "./ui/pages/PatientDataPage";
import PortalLoginPage from "./ui/pages/PortalLoginPage";
import PortalChooserPage from "./ui/pages/PortalChooserPage";
import PortalNoRecordsPage from "./ui/pages/PortalNoRecordsPage";
import PortalForgotPasswordPage from "./ui/pages/PortalForgotPasswordPage";
import PortalAgreementsPage from "./ui/pages/PortalAgreementsPage";
import PortalPaymentsPage from "./ui/pages/PortalPaymentsPage";
import PortalSystemicMapsPage from "./ui/pages/PortalSystemicMapsPage";
import PortalSystemicMapPage from "./ui/pages/PortalSystemicMapPage";
import PortalMeditationsPage from "./ui/pages/PortalMeditationsPage";
import PortalMeditationPage from "./ui/pages/PortalMeditationPage";

// Most of the portal is clinical surface, and consent is what makes it usable
// at all.
const consented = (page: React.ReactNode) => (
  <RequireCurrentConsent>{page}</RequireCurrentConsent>
);

export const portalRoutes: RouteObject[] = [
  // Public routes (no auth required)
  { path: "/portal/accept/:token", element: <AcceptInvitationPage /> },
  { path: "/portal/login", element: <PortalLoginPage /> },
  { path: "/portal/forgot-password", element: <PortalForgotPasswordPage /> },

  // Choosing a panel, and having none. Authenticated, but outside the guard:
  // the guard is what routes here, so wrapping them in it would loop.
  { path: "/portal/choose", element: <PortalChooserPage /> },
  { path: "/portal/no-records", element: <PortalNoRecordsPage /> },

  // Authenticated portal routes. The layout belongs to the route that holds
  // them, not to each page, so it stays mounted as the patient moves around:
  // the menu is asked for once rather than on every tap.
  {
    element: (
      <PatientAuthGuard>
        <PortalLayout>
          <Outlet />
        </PortalLayout>
      </PatientAuthGuard>
    ),
    children: [
      // Reachable without current consent: one is where it is given, the
      // other is where a patient takes their data with them.
      { path: "/portal/consent", element: <ConsentPage /> },
      { path: "/portal/data", element: <PatientDataPage /> },

      { path: "/portal", element: consented(<PortalDashboard />) },
      { path: "/portal/sessions/:id", element: <SessionAgreementsRedirect /> },
      { path: "/portal/notes", element: consented(<PatientNotesPage />) },
      { path: "/portal/payments", element: consented(<PortalPaymentsPage />) },
      { path: "/portal/systemic-maps", element: consented(<PortalSystemicMapsPage />) },
      { path: "/portal/systemic-maps/:mapId", element: consented(<PortalSystemicMapPage />) },
      { path: "/portal/meditations", element: consented(<PortalMeditationsPage />) },
      { path: "/portal/meditations/:meditationId", element: consented(<PortalMeditationPage />) },
      { path: "/portal/agreements", element: consented(<PortalAgreementsPage />) },
    ],
  },
];

/**
 * The portal's routes as `<Route>` elements, nesting included.
 *
 * The app declares its routes as JSX; mapping this list flat, as it is for
 * the other modules, would drop the pages that live under the layout.
 */
export function renderPortalRoutes(routes: RouteObject[] = portalRoutes): React.ReactNode[] {
  return routes.map((route, index) => (
    <Route key={route.path ?? `portal-layout-${index}`} path={route.path} element={route.element}>
      {route.children && renderPortalRoutes(route.children)}
    </Route>
  ));
}
